import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import parentEnUS from '../../../packages/localization/en-US.parent.json';
import parentFrFR from '../../../packages/localization/fr-FR.parent.json';
import childEnUS from '../../../packages/localization/en-US.json';
import childFrFR from '../../../packages/localization/fr-FR.json';

// Deep-merge child keys into parent translations. Parent keys win for
// conflicts (e.g. app.title) so parent pages render correctly. Child pages
// get their own unique keys (welcome.*, lesson.*, etc.) from the child file.
function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const out = { ...target };
  for (const k of Object.keys(source)) {
    if (typeof source[k] === 'object' && source[k] !== null && typeof target[k] === 'object' && target[k] !== null) {
      out[k] = deepMerge(target[k] as Record<string, unknown>, source[k] as Record<string, unknown>);
    } else if (!(k in target)) {
      out[k] = source[k];
    }
  }
  return out;
}

const enUS = deepMerge(parentEnUS as Record<string, unknown>, childEnUS as Record<string, unknown>);
const frFR = deepMerge(parentFrFR as Record<string, unknown>, childFrFR as Record<string, unknown>);

// Read stored language from localStorage directly — no LanguageDetector plugin.
// Replacing the plugin with a plain synchronous read eliminates the i18next v24
// timing window where the plugin's detect() result is not yet applied to
// i18n.language when the first getFixedT() snapshot is taken inside useTranslation.
function resolveLanguage(): 'en-US' | 'fr-FR' {
  try {
    const stored = localStorage.getItem('i18nextLng') ?? '';
    return stored.startsWith('fr') ? 'fr-FR' : 'en-US';
  } catch {
    return 'en-US';
  }
}

i18n
  .use(initReactI18next)
  .init({
    lng: resolveLanguage(),
    resources: {
      'en-US': { translation: enUS },
      'fr-FR': { translation: frFR },
    },
    fallbackLng: 'en-US',
    supportedLngs: ['en-US', 'fr-FR'],
    interpolation: { escapeValue: false },
  });

/** Keep <html lang> in sync with the active UI language (accessibility). */
function syncHtmlLang(lng: string): void {
  try {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lng.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    }
  } catch { /* no DOM (tests/SSR) */ }
}

// Persist every language change to localStorage so the choice survives reload,
// and keep <html lang> correct for assistive tech.
i18n.on('languageChanged', (lng) => {
  try {
    localStorage.setItem('i18nextLng', lng);
  } catch { /* ignore quota errors */ }
  syncHtmlLang(lng);
});
// Set <html lang> once for the initial language too.
syncHtmlLang(i18n.language || 'en-US');

export default i18n;
